import { useId, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactElement, ReactNode, RefObject } from 'react';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { X } from './icons';
import { Portal } from './Portal';
import { useEnterTransition } from './useEnterTransition';
import { useModalSurface } from './useModalSurface';

export type DialogSize = 'sm' | 'md' | 'lg';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Short supporting line under the title, wired to `aria-describedby`. */
  description?: string;
  children?: ReactNode;
  /** Pinned below the scroll area. Usually a `<DialogFooter>`. */
  footer?: ReactNode;
  size?: DialogSize;
  dismissOnBackdrop?: boolean;
  hideCloseButton?: boolean;
  closeLabel?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
}

const SIZE: Record<DialogSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
};

export interface DialogFooterProps {
  children: ReactNode;
  className?: string;
}

export function DialogFooter({ children, className }: DialogFooterProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-2 border-t border-border',
        'bg-surface-sunken px-4 py-3 sm:px-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

function DialogSurface({
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissOnBackdrop = true,
  hideCloseButton = false,
  closeLabel = 'Close',
  initialFocusRef,
  className,
}: Omit<DialogProps, 'open'>): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const entered = useEnterTransition();
  const { onKeyDown } = useModalSurface({ panelRef, onClose, initialFocusRef });

  function handleBackdropMouseDown(event: ReactMouseEvent<HTMLDivElement>): void {
    const target = event.target;
    if (target instanceof Node && panelRef.current?.contains(target)) return;
    if (dismissOnBackdrop) {
      onClose();
      return;
    }
    // Keep the keydown handler reachable when a stray click blurs the panel.
    panelRef.current?.focus({ preventScroll: true });
  }

  return (
    <div
      onKeyDown={onKeyDown}
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6"
    >
      <div
        aria-hidden="true"
        className={cn(
          'absolute inset-0 bg-canvas/75 transition-opacity duration-150 motion-reduce:transition-none',
          entered ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden border border-border',
          'bg-surface text-ink shadow-pop outline-none',
          'rounded-t-panel sm:max-h-[85vh] sm:rounded-panel',
          'transition duration-150 motion-reduce:transition-none',
          entered
            ? 'translate-y-0 opacity-100 sm:scale-100'
            : 'translate-y-4 opacity-0 sm:translate-y-0 sm:scale-[0.98]',
          SIZE[size],
          className,
        )}
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-3 sm:px-5">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-sm font-semibold text-ink">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-[13px] leading-5 text-ink-muted">
                {description}
              </p>
            )}
          </div>
          {!hideCloseButton && (
            <IconButton
              label={closeLabel}
              icon={<X />}
              compact
              tooltip={false}
              onClick={onClose}
              className="-mr-2 -mt-1"
            />
          )}
        </div>

        <div className="fs-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4 text-sm sm:px-5">
          {children}
        </div>

        {footer}

        {/* Keeps the sheet clear of the home indicator; desktop needs no inset. */}
        <div aria-hidden="true" className="fs-safe-bottom sm:hidden" />
      </div>
    </div>
  );
}

/**
 * Modal dialog. Below `sm` it becomes a bottom sheet, which is the only shape
 * that works one-handed on a phone.
 */
export function Dialog({ open, ...rest }: DialogProps): ReactElement | null {
  if (!open) return null;
  return (
    <Portal>
      <DialogSurface {...rest} />
    </Portal>
  );
}
